/**
 * use-autosave.ts — Tự lưu nháp có debounce cho màn cấu hình nhân viên AI.
 *
 * - Gom thay đổi, đợi `delayMs` không gõ nữa mới lưu; chỉ gửi trường đã đổi.
 * - Một lượt lưu một lúc; thay đổi trong lúc đang lưu sẽ được lưu tiếp ngay sau.
 * - `flush()` lưu ngay (trước khi phát hành / rời trang).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { changedFields } from './bot-settings'
import { i18n, currentLocale } from '@/lib/i18n'

export type AutosaveStatus =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'saving' }
  | { state: 'saved'; at: Date }
  | { state: 'error'; message: string }

export function useAutosave<T extends Record<string, unknown>>(opts: {
  value: T | null
  save: (patch: Partial<T>) => Promise<void>
  delayMs?: number
  /** Không lưu khi trả false (vd còn lỗi phía giao diện). */
  canSave?: (value: T) => boolean
  errorMessage?: (err: unknown) => string
}) {
  const { value, delayMs = 1200 } = opts
  const [status, setStatus] = useState<AutosaveStatus>({ state: 'idle' })
  const savedRef = useRef<T | null>(null)
  const latestRef = useRef<T | null>(value)
  const busyRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const optsRef = useRef(opts)
  optsRef.current = opts
  latestRef.current = value

  /** Đặt mốc "đã lưu" (khi tải bot lần đầu / sau khi khôi phục phiên bản). */
  const reset = useCallback((v: T) => {
    savedRef.current = v
    latestRef.current = v
    if (timerRef.current) clearTimeout(timerRef.current)
    setStatus({ state: 'idle' })
  }, [])

  const run = useCallback(async (): Promise<boolean> => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    const cur = latestRef.current
    const base = savedRef.current
    if (!cur || !base) return true
    const patch = changedFields(base, cur)
    if (Object.keys(patch).length === 0) return true
    if (optsRef.current.canSave && !optsRef.current.canSave(cur)) return false
    if (busyRef.current) return false
    busyRef.current = true
    setStatus({ state: 'saving' })
    try {
      await optsRef.current.save(patch)
      savedRef.current = cur
      setStatus({ state: 'saved', at: new Date() })
      busyRef.current = false
      // Có thay đổi mới trong lúc đang lưu → lưu tiếp.
      if (latestRef.current !== cur) return run()
      return true
    } catch (err) {
      busyRef.current = false
      setStatus({ state: 'error', message: optsRef.current.errorMessage?.(err) ?? i18n.t('aiTrainLib:useAutosave.failed') })
      return false
    }
  }, [])

  useEffect(() => {
    if (!value || !savedRef.current) return
    if (Object.keys(changedFields(savedRef.current, value)).length === 0) return
    setStatus((s) => (s.state === 'saving' ? s : { state: 'pending' }))
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { void run() }, delayMs)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [value, delayMs, run])

  // Rời trang khi còn thay đổi chưa lưu → hỏi lại.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      const cur = latestRef.current
      const base = savedRef.current
      if (cur && base && Object.keys(changedFields(base, cur)).length > 0) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [])

  return { status, flush: run, reset }
}

/** "Lưu gần đây: 14:32" / "Đang lưu…" … */
export function autosaveLabel(s: AutosaveStatus): string {
  switch (s.state) {
    case 'idle': return i18n.t('aiTrainLib:useAutosave.idle')
    case 'pending': return i18n.t('aiTrainLib:useAutosave.pending')
    case 'saving': return i18n.t('aiTrainLib:useAutosave.saving')
    case 'saved': return i18n.t('aiTrainLib:useAutosave.saved', { time: s.at.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' }) })
    case 'error': return i18n.t('aiTrainLib:useAutosave.error', { message: s.message })
  }
}
